import React, { useMemo, useRef } from 'react';
import classnames from 'classnames';

import './ModalFormCategoryLayout.css';
import ModalFormCollapsibleSection from './ModalFormCollapsibleSection';
import ModalFormCategoryNavButton from './ModalFormCategoryNavButton';
import useCollapsesScrollHeight from './useCollapsesScrollHeight';
import useModalFormCategoryAnchor, {
  getOrderedSections,
} from './useModalFormCategoryAnchor';

/**
 * Layout em duas colunas para abas de resumo em modal: categorias à esquerda, conteúdo à direita.
 *
 * Todas as seções ficam montadas na ordem das categorias (`sectionIds` de cada uma).
 * Ao clicar numa categoria, rola até o primeiro collapse dessa categoria.
 * Ao rolar manualmente, a nav destaca a categoria da seção visível.
 *
 * - **categories**: `{ id, label, sectionIds[] }` — ordem das categorias define a ordem no scroll.
 *   Só aparece na nav se existir ao menos uma seção renderizada com id listado.
 * - **sections**: blocos disponíveis; cada um vira um `ModalFormCollapsibleSection`.
 *   Por padrão todos os collapses ficam abertos; o usuário pode fechar individualmente.
 *
 * @typedef {{ id: string, label: React.ReactNode, sectionIds: string[], badge?: { text: string, variant?: 'default' | 'doing' | 'done' } }} ModalFormCategory
 * @typedef {{ id: string, header: React.ReactNode, open?: boolean, children: React.ReactNode }} ModalFormSection
 *
 * @param {{ categories: ModalFormCategory[], sections: ModalFormSection[], className?: string, layoutSyncKey?: number, collapsible?: boolean, hideShadow?: boolean, mainHeader?: React.ReactNode, mainContent?: React.ReactNode }} props
 */
function ModalFormCategoryLayout({
  categories,
  sections,
  className,
  layoutSyncKey = 0,
  collapsible = true,
  hideShadow = false,
  mainHeader,
  mainContent,
}) {
  const sectionById = useMemo(() => {
    const map = new Map();
    (sections || []).forEach((s) => map.set(s.id, s));
    return map;
  }, [sections]);

  const visibleCategories = useMemo(() => {
    if (mainContent) {
      return categories || [];
    }

    return (categories || []).filter((category) =>
      category.sectionIds.some((sectionId) => sectionById.has(sectionId))
    );
  }, [categories, sectionById, mainContent]);

  const orderedSections = useMemo(
    () => getOrderedSections(visibleCategories, sectionById),
    [visibleCategories, sectionById]
  );

  const orderedSectionIds = useMemo(
    () => orderedSections.map((section) => section.id),
    [orderedSections]
  );

  const mainRef = useRef(null);

  const { resolvedCategoryId, scrollToCategory } = useModalFormCategoryAnchor({
    mainRef,
    visibleCategories,
    sectionById,
    orderedSectionIds,
    layoutSyncKey,
  });

  useCollapsesScrollHeight(mainRef, [
    orderedSections.length,
    layoutSyncKey,
    Boolean(mainHeader),
    Boolean(mainContent),
  ]);

  if (!mainContent && (!sections?.length || !orderedSections.length)) {
    return null;
  }

  return (
    <div className={classnames('modalFormCategoryLayout', className)}>
      {visibleCategories.length > 0 && (
        <nav className="modalFormCategoryLayout__nav">
          <ul className="modalFormCategoryLayout__navList">
            {visibleCategories.map((category) => (
              <li
                key={category.id}
                className="modalFormCategoryLayout__navItem"
              >
                <ModalFormCategoryNavButton
                  category={category}
                  isActive={resolvedCategoryId === category.id}
                  onClick={() => scrollToCategory(category.id)}
                />
              </li>
            ))}
          </ul>
        </nav>
      )}
      <div
        ref={mainRef}
        className={classnames('modalFormCategoryLayout__main', {
          'modalFormCategoryLayout__main--withHeader': mainHeader,
        })}
      >
        {mainHeader && (
          <div
            className={classnames('modalFormCategoryLayout__mainHeader', {
              'modalFormCategoryLayout__mainHeader--divider': mainContent,
            })}
          >
            {mainHeader}
          </div>
        )}
        <div className="modalFormCategoryLayout__collapses">
          {mainContent ||
            orderedSections.map((section) => (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className="modalFormCategoryLayout__section"
              >
                <ModalFormCollapsibleSection
                  header={section.header}
                  className={section.className}
                  open={collapsible ? section.open ?? true : true}
                  collapsible={collapsible}
                  hideShadow={hideShadow}
                  collapseCallback={section.collapseCallback}
                  headerActions={section.headerActions}
                  headerStyle={section.headerStyle}
                  styleTitle={
                    section.styleTitle ?? {
                      fontSize: 18,
                      fontWeight: 700,
                      color: '#09090B',
                    }
                  }
                >
                  {section.children}
                </ModalFormCollapsibleSection>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default ModalFormCategoryLayout;
